<template>
  <div class="login-screen">
    <div class="login-card">
      <h1 class="login-title">REGISTRO CLIENTES</h1>
      <p class="login-sub">Crea tu cuenta para reservar funciones</p>

      <div v-if="errorMessage" class="login-error">{{ errorMessage }}</div>

      <!-- Cuenta creada y pendiente de confirmar el correo -->
      <template v-if="pendingVerification">
        <div class="login-success">{{ successMessage }}</div>
        <p class="login-hint">
          Si no te llega en unos minutos, revisa tu carpeta de spam o pide otro.
        </p>
        <div v-if="resentMessage" class="login-success">{{ resentMessage }}</div>
        <button
          v-else
          type="button"
          class="login-btn"
          :disabled="resending"
          @click="handleResend"
        >
          {{ resending ? 'ENVIANDO…' : 'REENVIAR CORREO' }}
        </button>
        <a href="/login/" class="login-forgot">← Ir a iniciar sesión</a>
      </template>

      <form v-else class="login-form" @submit.prevent="handleSubmit">
        <div class="login-row">
          <input
            v-model="firstName"
            class="login-input"
            type="text"
            placeholder="Nombres"
            autocomplete="given-name"
          />
          <input
            v-model="lastName"
            class="login-input"
            type="text"
            placeholder="Apellidos"
            autocomplete="family-name"
          />
        </div>
        <input
          v-model="email"
          class="login-input"
          type="email"
          placeholder="Email"
          autocomplete="email"
        />
        <input
          v-model="password"
          class="login-input"
          type="password"
          placeholder="Contraseña"
          autocomplete="new-password"
        />
        <input
          v-model="password2"
          class="login-input"
          type="password"
          placeholder="Repite la contraseña"
          autocomplete="new-password"
        />
        <button type="submit" class="login-btn" :disabled="loading">
          {{ loading ? 'CREANDO CUENTA…' : 'REGISTRARSE' }}
        </button>
      </form>

      <template v-if="!pendingVerification">
        <div class="login-divider"></div>

        <p class="login-register-text">¿YA TIENES CUENTA?</p>
        <a href="/login/" class="login-register">INICIAR SESIÓN</a>
      </template>
    </div>
  </div>
</template>

<script>
import { resendVerificationEmail } from "@/services/teatroApi";

export default {
  name: "RegisterTeatro",
  data() {
    return {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      password2: "",
      loading: false,
      errorMessage: "",
      // Con la verificación de correo activada, el registro no inicia sesión:
      // deja esta pantalla esperando que el usuario abra el enlace.
      pendingVerification: false,
      successMessage: "",
      resending: false,
      resentMessage: "",
    };
  },
  methods: {
    async handleSubmit() {
      if (!this.firstName || !this.email || !this.password) {
        this.errorMessage = "Completa tu nombre, correo y contraseña.";
        return;
      }
      if (this.password.length < 6) {
        this.errorMessage = "La contraseña debe tener al menos 6 caracteres.";
        return;
      }
      if (this.password !== this.password2) {
        this.errorMessage = "Las contraseñas no coinciden.";
        return;
      }

      this.loading = true;
      this.errorMessage = "";

      try {
        const formData = new FormData();
        formData.append("first_name", this.firstName);
        formData.append("last_name", this.lastName);
        formData.append("email", this.email);
        formData.append("password", this.password);

        const response = await fetch("/register_user/", {
          method: "POST",
          body: formData,
          headers: {
            "X-Requested-With": "XMLHttpRequest",
          },
        });

        const data = await response.json().catch(() => ({}));
        if (response.ok && data.success && data.verification_required) {
          this.pendingVerification = true;
          this.successMessage = data.message;
          if (!data.email_sent) this.resentMessage = "";
        } else if (response.ok && data.success) {
          window.location.href = data.redirect_url || "/";
        } else {
          this.errorMessage = data.error || "No se pudo crear la cuenta.";
        }
      } catch (error) {
        console.error("Error en el registro:", error);
        this.errorMessage = "Ocurrió un error al crear la cuenta.";
      } finally {
        this.loading = false;
      }
    },
    async handleResend() {
      this.resending = true;
      this.errorMessage = "";
      try {
        const data = await resendVerificationEmail(this.email);
        this.resentMessage =
          data.message || "Te enviamos un correo nuevo con el enlace.";
      } catch (error) {
        this.errorMessage =
          error.response?.data?.error || "No pudimos enviar el correo.";
      } finally {
        this.resending = false;
      }
    },
  },
};
</script>

<style scoped>
.login-screen {
  min-height: calc(100vh - 40px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
}

.login-card {
  width: 100%;
  max-width: 440px;
  background: rgba(10, 6, 6, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 22px;
  padding: 40px 38px 34px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.6);
  text-align: center;
  font-family: var(--font-family-primary);
  backdrop-filter: blur(2px);
}

.login-title {
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 20px;
  font-weight: 800;
  letter-spacing: 2px;
  margin: 0 0 8px;
}

.login-sub {
  color: rgba(255, 255, 255, 0.7);
  font-size: 11px;
  letter-spacing: 0.4px;
  margin: 0 0 26px;
}

/* Nota bajo el aviso de correo enviado: más grande y clara que .login-sub
   para que se lea bien sobre el fondo del telón. */
.login-hint {
  color: rgba(255, 255, 255, 0.9);
  font-size: 12.5px;
  line-height: 1.5;
  margin: 0 0 22px;
}

.login-error {
  background: rgba(200, 16, 46, 0.15);
  border: 1px solid rgba(200, 16, 46, 0.5);
  color: #ffb3b3;
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 12px;
  margin-bottom: 16px;
}

.login-success {
  background: rgba(46, 160, 87, 0.15);
  border: 1px solid rgba(46, 160, 87, 0.5);
  color: #c9f5d8;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 13.5px;
  line-height: 1.5;
  margin-bottom: 16px;
  overflow-wrap: anywhere;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.login-row {
  display: flex;
  gap: 14px;
}

.login-row .login-input {
  flex: 1;
  min-width: 0;
}

.login-input {
  width: 100%;
  background: #fff;
  border: none;
  border-radius: 8px;
  padding: 13px 16px;
  font-family: var(--font-family-primary);
  font-size: 13px;
  color: #1a1a1a;
  outline: none;
}

.login-input::placeholder {
  color: #9a9a9a;
}

.login-btn {
  margin-top: 6px;
  width: 100%;
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 13px;
  font-family: var(--font-family-heading);
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 1px;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(200, 16, 46, 0.4);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.login-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(200, 16, 46, 0.5);
}

.login-btn:disabled {
  opacity: 0.6;
  cursor: default;
  transform: none;
  box-shadow: none;
}

.login-forgot {
  display: inline-block;
  margin-top: 18px;
  color: rgba(255, 255, 255, 0.75);
  font-size: 11px;
  text-decoration: none;
  transition: color 0.2s ease;
}

.login-forgot:hover {
  color: #fff;
}

.login-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.15);
  margin: 24px 0 18px;
}

.login-register-text {
  color: rgba(255, 255, 255, 0.7);
  font-size: 11px;
  letter-spacing: 0.5px;
  margin: 0 0 6px;
}

.login-register {
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 16px;
  font-weight: 800;
  letter-spacing: 1px;
  text-decoration: none;
  transition: color 0.2s ease;
}

.login-register:hover {
  color: #ff6b6b;
}

@media (max-width: 480px) {
  .login-row {
    flex-direction: column;
  }
}
</style>
